import * as React from 'react';
import { TouchableOpacity, View } from 'react-native';

import type * as api from '@gitbook/api-client';
import { flex, spacing, themedFonts, useCurrentTheme } from '@gitbook/react-uikit';
import { Description, FieldGroup, Group, Label } from '@gitbook/spine-react';

import { ContentKitSelect } from './ContentKitSelect';
import { ContentKitTextStyleContext } from './ContentKitText';
import type { ContentKitElementProps, ContentKitElementRef } from './types';

export function ContentKitInput(
    props: ContentKitElementProps<api.ContentKitInput>
): React.ReactElement {
    const { element, renderElement } = props;

    const ref = React.useRef<ContentKitElementRef>(null);

    const [, font] = useCurrentTheme(themedFonts);

    const label = <Label>{element.label}</Label>;
    const hint = element.hint ? (
        <ContentKitTextStyleContext.Provider value={[font.Label, font.textMid]}>
            <Description>
                {typeof element.hint === 'string' ? element.hint : renderElement(element.hint)}
            </Description>
        </ContentKitTextStyleContext.Provider>
    ) : null;

    const onPress = () => {
        if (ref.current) {
            ref.current.press?.();
        }
    };

    // Vertical
    if (['textinput', 'codeblock'].includes(element.element.type)) {
        return (
            <View>
                <View style={[spacing.Margin.bottom100]}>{label}</View>
                {hint ? <View style={[spacing.Margin.bottom200]}>{hint}</View> : null}
                {renderElement(element.element)}
            </View>
        );
    }

    if (['radio', 'checkbox'].includes(element.element.type)) {
        // Horizontal, input on the left
        return (
            <View style={[flex.row, flex.alignItemsCenter]}>
                <View style={[spacing.Padding.right400]}>
                    {renderElement(element.element, ref)}
                </View>
                <TouchableOpacity onPress={onPress} style={[flex.flex1]}>
                    <View style={[hint && spacing.Margin.bottom100]}>{label}</View>
                    {hint}
                </TouchableOpacity>
            </View>
        );
    }

    // Use FieldGroup for OpenAPI spec select
    // Can be used with other elements when migrated to Spine
    if (
        element.element.type === 'select' &&
        'source' in element.element.options &&
        element.element.options.source === 'openapi'
    ) {
        return (
            <FieldGroup orientation="horizontal">
                <Group>
                    {label}
                    {hint}
                </Group>
                {renderElement(element.element)}
            </FieldGroup>
        );
    }

    // Horizontal, input on the right
    return (
        <View style={[flex.row, flex.alignItemsCenter]}>
            <TouchableOpacity onPress={onPress} style={[flex.flex1]}>
                <View style={[hint && spacing.Margin.bottom100]}>{label}</View>
                {hint}
            </TouchableOpacity>
            <View
                style={[
                    spacing.Padding.left400,
                    /**
                     * Select inputs can become arbitrarily wide which doesn't look good
                     * in a horizontal layout. So, limit the width to 40% of the available
                     * space. Rest of the space is used by the label.
                     */
                    element.element.type === 'select' ? { flex: 0.4 } : undefined,
                ]}
            >
                {element.element.type === 'select' ? (
                    <ContentKitSelect
                        {...props}
                        element={element.element}
                        /**
                         * The custom style is needed to prevent the select from overflowing.
                         * Ideally we should be able to remove this once we have spine components
                         * properly implemented to take care of this (horizontal layout, overflow, etc.)
                         */
                        style={{ width: 'min-content', maxWidth: '100%', alignSelf: 'flex-end' }}
                    />
                ) : (
                    renderElement(element.element)
                )}
            </View>
        </View>
    );
}
